// MACHINERY V2 (Stroje) — active-filter chip descriptors for the katalog's
// pattern B row 2 (M2.16c).
//
// The katalog counterpart of fleet/fleetActiveFilters.ts, and deliberately the
// same shape: turn APPLIED filter state into "Vyrobca: Mazak ✕" summaries, and
// own what each ✕ undoes.
//
// >>> ONE CHIP PER FILTER GROUP, AND THAT COUNT IS ALSO THE Filtre BADGE. <<<
// Derived from ONE list so the two can never disagree about how many filters
// are on — the rule the board draws (badge "2" over two chips) and the rule
// FleetMobileChrome.tsx:19-21 already states.
//
// >>> SEARCH IS NOT A CHIP — AND THAT IS WHY THIS COUNT IS NOT
// `catalogActiveFilterCount`. <<< The search box is always on screen in row 1,
// so a chip for it would be a second, removable copy of a visible control (the
// fleet's rule, fleetActiveFilters.ts:17-19). `catalogActiveFilterCount`
// (catalogQuery.ts) DOES count search, because the desktop surface it serves has
// no chip row to show the search in — it prints one number above the grid. The
// two numbers therefore differ by one while a search is committed, and no user
// can see both: the line is `isDesktop`-only and the badge is below 1280. The
// desktop count is NOT changed to match; it is correct for its own surface.
//
// >>> EVERY REMOVAL USES THE UPDATER FORM, NEVER A RENDER SNAPSHOT. <<<
// `CatalogFilterRail`'s own `onQueryChange` prop doc says why in full: the rail's
// range inputs publish on a 350 ms debounce (catalogRailAtoms.tsx
// `RANGE_PUBLISH_DEBOUNCE_MS`), so a publish can land AFTER this row rendered,
// and a `{...query, x: undefined}` built from this render's `query` would spread
// a stale snapshot back over it and silently undo the number the user just
// typed. This is the same defect fleetActiveFilters.ts:21-35 records for the
// fleet, where the merge base is a ref because the state is in Redux; here the
// state is local, so React's own updater form is the fix. Any NEW handler in
// this file takes `(prev) => …`.
//
// RANGE VALUES ARE SYMBOLS, NOT SENTENCES. "1 000 – 5 000" / "1 000 +" /
// "≤ 5 000" need no per-language string, so an open-ended range costs no key.
// The GROUP names reuse the rail's existing labels, so a chip can never name a
// filter differently from the control that set it.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";

import useTranslation from "../../../../shared/hooks/useTranslation";
import { getReferenceLabel } from "../../../../shared/utils/machineReferenceLabels";
import { CatalogBrowseQuery } from "../../models/models";
import { ActiveFilterChip } from "../mobile/ActiveFilterChips";
import { clearCatalogFilters, withCatalogFilter } from "./catalogQuery";
import useCatalogBrowse from "./useCatalogBrowse";

type SetQuery = React.Dispatch<React.SetStateAction<CatalogBrowseQuery>>;

export interface CatalogActiveFilters {
  chips: ActiveFilterChip[];
  clearAll: () => void;
}

export const useCatalogActiveFilters = (
  c: ReturnType<typeof useCatalogBrowse>
): CatalogActiveFilters => {
  const { translate, getCurrentLanguage } = useTranslation("myMachinery");
  const query = c.query;
  const setQuery: SetQuery = c.setQuery;
  const lang = getCurrentLanguage();

  const num = (value?: number): string =>
    value === undefined ? "" : value.toLocaleString(lang);

  /** "lo – hi" / "lo +" / "≤ hi". One bound present is still one filter. */
  const rangeText = (lo?: number, hi?: number): string => {
    if (lo !== undefined && hi !== undefined) return `${num(lo)} – ${num(hi)}`;
    if (lo !== undefined) return `${num(lo)} +`;
    return `≤ ${num(hi)}`;
  };

  const chips: ActiveFilterChip[] = [];

  const push = (id: string, label: string, patch: Partial<CatalogBrowseQuery>) => {
    chips.push({
      id,
      label,
      // withCatalogFilter also returns to page 1, which is what dropping a
      // filter must do — page 7 of the unfiltered set is not where the user was.
      onRemove: () => setQuery((prev) => withCatalogFilter(prev, patch)),
    });
  };

  if (query.manufacturerId) {
    const maker = c.manufacturers.find((m) => m.id === query.manufacturerId);
    push(
      "manufacturer",
      `${translate("myMachinery:manufacturer")}: ${
        maker ? maker.name : query.manufacturerId
      }`,
      { manufacturerId: undefined }
    );
  }

  if (query.minRpm !== undefined || query.maxRpm !== undefined) {
    push(
      "rpm",
      `${translate("myMachinery:spindleRpmRange")}: ${rangeText(query.minRpm, query.maxRpm)}`,
      { minRpm: undefined, maxRpm: undefined }
    );
  }

  if (query.minHp !== undefined || query.maxHp !== undefined) {
    push(
      "hp",
      `${translate("myMachinery:spindleHpRange")}: ${rangeText(query.minHp, query.maxHp)}`,
      { minHp: undefined, maxHp: undefined }
    );
  }

  if (query.minTools !== undefined || query.maxTools !== undefined) {
    push(
      "tools",
      `${translate("myMachinery:v2.catalog.toolCountRange")}: ${rangeText(
        query.minTools,
        query.maxTools
      )}`,
      { minTools: undefined, maxTools: undefined }
    );
  }

  if (query.taperTypeId) {
    const taper = c.taperTypes.find((t) => t.id === query.taperTypeId);
    push(
      "taper",
      `${translate("myMachinery:taperType")}: ${taper ? getReferenceLabel("taper", taper, translate) : query.taperTypeId}`,
      { taperTypeId: undefined }
    );
  }

  if (query.controlSystemBrandId) {
    const brand = c.controlSystemBrands.find((b) => b.id === query.controlSystemBrandId);
    push(
      "controlBrand",
      `${translate("myMachinery:controlSystemBrand")}: ${
        brand ? getReferenceLabel("brand", brand, translate) : query.controlSystemBrandId
      }`,
      { controlSystemBrandId: undefined }
    );
  }

  if (query.operationTypeId) {
    const op = c.operationTypes.find((o) => o.id === query.operationTypeId);
    push(
      "operationType",
      `${translate("myMachinery:operationType")}: ${op ? getReferenceLabel("operation", op, translate) : query.operationTypeId}`,
      { operationTypeId: undefined }
    );
  }

  // The two DR-1 / DR-2 booleans are their own label — "Filter: OEM ready" would
  // be a prefix restating the word "filter" the chip row already means. They are
  // cleared to `undefined`, NEVER `false`: the params are tri-state on the wire
  // and a literal `false` is a value the backend reads as a filter
  // (catalogQuery.ts `toggleCatalogFlag`).
  if (query.oemReady) {
    push("oemReady", translate("myMachinery:oemReady"), { oemReady: undefined });
  }

  if (query.machineIdIntegrated) {
    push("machineIdIntegrated", translate("myMachinery:machineIdIntegrated"), {
      machineIdIntegrated: undefined,
    });
  }

  // Clear-all drops the SEARCH too, which no chip represents — `clearSearch`
  // rather than a query edit, because the box, the committed value and the
  // pending 300 ms debounce have to move together (useCatalogBrowse.ts's "EVERY
  // CLEAR PATH GOES THROUGH clearSearch" note). Doing only the query half is
  // what lets a keystroke re-apply the search a moment after Clear all wiped it.
  const clearAll = () => {
    c.clearSearch();
    c.setBrandSearch("");
    setQuery((prev) => clearCatalogFilters(prev));
  };

  return { chips, clearAll };
};

export default useCatalogActiveFilters;
